import React from 'react';
import { Activity, Edit, X, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';

const CLINICAL_FIELDS = [
  { key: 'birth_history', label: 'Birth History' },
  { key: 'immunization_history', label: 'Immunization History' },
  { key: 'past_medical_history', label: 'Past Medical History' },
  { key: 'past_surgical_history', label: 'Past Surgical History' },
  { key: 'obgyne_menstrual_history', label: 'OB-Gyne & Menstrual History' },
  { key: 'sexual_history', label: 'Sexual History' },
  { key: 'medications', label: 'Medication History' },
  { key: 'allergies', label: 'Known Allergies' }
];

const ClinicalHistorySection = ({
  editedData,
  isEditing,
  isCollapsed,
  onToggleEdit,
  onCancelEdit,
  onToggleCollapse,
  onUpdateField
}) => {
  return (
    <div className="mb-6">
      <Collapsible open={!isCollapsed} onOpenChange={onToggleCollapse}>
        <div className="card border-2 border-indigo-200 bg-gradient-to-br from-white to-indigo-50 shadow-lg">
          <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-indigo-100">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="clinical-history-collapse-btn">
                <ChevronRight
                  size={20}
                  className={`text-indigo-600 transition-transform duration-200 ${!isCollapsed ? 'rotate-90' : ''}`}
                />
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Activity size={20} className="text-indigo-600" />
                  Clinical History
                </h2>
              </button>
            </CollapsibleTrigger>
            {!isEditing ? (
              <Button onClick={onToggleEdit} className="btn-secondary" data-testid="clinical-history-edit-btn">
                <Edit size={16} className="mr-2" />
                Edit
              </Button>
            ) : (
              <Button onClick={onCancelEdit} className="btn-ghost text-red-600" data-testid="clinical-history-cancel-btn">
                <X size={16} className="mr-2" />
                Cancel
              </Button>
            )}
          </div>

          <CollapsibleContent>
            {!isEditing ? (
              <div className="space-y-4">
                {CLINICAL_FIELDS.map(section => (
                  <div key={section.key} className="p-3 bg-white rounded-lg border border-indigo-200">
                    <p className="text-xs font-semibold text-indigo-900 uppercase tracking-wider mb-2">{section.label}</p>
                    {editedData.health_record?.[section.key] ? (
                      <p className="text-slate-700 whitespace-pre-wrap">{editedData.health_record[section.key]}</p>
                    ) : (
                      <p className="text-slate-500 italic text-sm">No data</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {CLINICAL_FIELDS.map(section => (
                  <AccordionItem key={section.key} value={section.key}>
                    <AccordionTrigger>{section.label}</AccordionTrigger>
                    <AccordionContent>
                      <Textarea
                        value={editedData.health_record?.[section.key] || ''}
                        onChange={(e) => onUpdateField('health_record', section.key, e.target.value)}
                        className="w-full"
                        rows={6}
                        placeholder={`Enter ${section.label.toLowerCase()}...`}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};

export default ClinicalHistorySection;
